import { useEffect, useState } from 'react';

import {
  useAddPostReaction,
  useDeletePost,
  usePostPutPost,
  usePostPutPostComment,
} from '../../../../services/redux/api/postsApi';
import { IPost } from '../../../../types/interfaces/post';
import { useLayout } from '../../../../context/LayoutContext';
import { IComment } from '../../../../types/interfaces/comment';
import { useGetMyDetails } from '../../../../services/redux/api/usersApi';
import { useFileList } from '../../../../hooks/use-file-list';

const showCommentByDefault = 3;
const showMoreCommentsCount = 5;

export function usePost(post: IPost) {
  const [addPostReaction] = useAddPostReaction();
  const [postPutPostComment] = usePostPutPostComment();
  const [postPutPost] = usePostPutPost();
  const [deletePost] = useDeletePost();
  const { data: user } = useGetMyDetails();

  const {
    fileIdsToDelete,
    filesToUpload,
    resultFiles: postFiles,
    onDeleteFile,
    onUploadFiles,
    resetFilesState,
  } = useFileList(post.files);

  const {
    filesToUpload: commentFilesToUpload,
    onDeleteFile: onDeleteCommentFile,
    onUploadFiles: onUploadCommentFiles,
    resetFilesState: resetCommentFilesState,
  } = useFileList([]);

  const { showLoader, hideLoader } = useLayout();
  const [editedPost, setEditedPost] = useState<IPost>(null);
  const [newComment, setNewComment] = useState<IComment>({
    content: '',
    postId: post.id,
    parent: null,
  });
  const [userReaction, setUserReaction] = useState<boolean>(null);
  const [showedCommentsCount, setShowedCommentsCount] =
    useState(showCommentByDefault);

  useEffect(() => {
    if (userReaction === null) {
      setUserReaction(
        !!post.reactions?.find((reaction) => reaction.userId === user.id),
      );
    }
  }, [post]);

  function onToggleToEditPost() {
    setEditedPost(post);
  }

  function onPostEdit(newValue: string) {
    setEditedPost({ ...post, content: newValue });
  }

  function onCommentEdit(newValue: string) {
    setNewComment({ ...newComment, content: newValue });
  }

  function onCommentReply(comment: IComment) {
    setNewComment({ ...newComment, parent: comment, parentId: comment.id });
  }

  function onCommentDeleteFromReply() {
    setNewComment({ ...newComment, parent: null, parentId: null });
  }

  function onCommentSave() {
    const loaderId = showLoader();

    return postPutPostComment({
      postId: post.id,
      body: { comment: newComment, filesToUpload: commentFilesToUpload },
    })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        hideLoader(loaderId);
        setNewComment({ content: '', postId: post.id, parent: null });
        setShowedCommentsCount((prev) => prev + 1);
        resetCommentFilesState();
      });
  }

  function onPostReaction() {
    return addPostReaction({ id: post.id })
      .then(() => setUserReaction((prev) => !prev))
      .catch((e) => console.log(e));
  }

  function onPostSave() {
    const loaderId = showLoader();

    return postPutPost({
      body: { post: editedPost, fileIdsToDelete, filesToUpload },
      id: post.id,
    })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        hideLoader(loaderId);
        setEditedPost(null);
        resetFilesState();
      });
  }

  function onPostDelete() {
    const loaderId = showLoader();

    return deletePost({ id: post.id })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        hideLoader(loaderId);
      });
  }

  function onShowMore() {
    setShowedCommentsCount((prev) =>
      prev + showMoreCommentsCount < post.comments.length
        ? prev + showMoreCommentsCount
        : post.comments.length,
    );
  }

  return {
    onToggleToEditPost,
    onPostEdit,
    onPostSave,
    onPostDelete,
    onPostReaction,
    onCommentSave,
    onCommentEdit,
    onCommentReply,
    onCommentDeleteFromReply,
    onUploadFiles,
    onDeleteFile,
    onDeleteCommentFile,
    onUploadCommentFiles,
    onShowMore,
    editedPost,
    newComment,
    postFiles,
    commentFiles: commentFilesToUpload,
    userReaction,
    user,
    showedCommentsCount,
  };
}
